<template>
  <div data-page-name="press-release">
    <div class="wrapper">
      <div class="content">
        <AppBreadcrumbs :items="[title]" append accent="light" />

        <h1 class="title">{{ title }}</h1>

        <strong v-if="subtitle" class="subtitle">
          {{ subtitle }}
        </strong>

        <hr />

        <div class="tags">
          <AppTag
            v-if="isString(location)"
            class="location"
            variant="secondary"
          >
            <SvgIcon name="location" />
            <address>{{ location }}</address>
          </AppTag>

          <AppTag
            v-if="isString(publishedAt)"
            class="published-at"
            variant="primary"
          >
            <SvgIcon name="calendar-today" />

            <time :datetime="publishedAt" pubdate>
              {{ $dayjs(publishedAt).format('MMMM D, YYYY') }}
            </time>
          </AppTag>
        </div>

        <div v-html="markup" class="markup" />
      </div>

      <AboutObjectFirst />
    </div>
  </div>
</template>

<script setup lang="ts">
import { createError } from '#app';

// components
import AppTag from '~/components/redesign/AppTag.vue';
import AppBreadcrumbs from '~/components/redesign/AppBreadcrumbs.vue';
import AboutObjectFirst from '~/components/redesign/AboutObjectFirst.vue';

// constants
import { STATUS_CODES } from '~/constants';

// utils
import get from 'lodash.get';
import isString from '~/utils/isString';

// types
import { PostStatus } from '~/types';

const {
  params: { slug },
  query: { preview },
} = useRoute();

const { data, error } = await useAsyncGql({
  operation: 'BlogArticle',
  variables: {
    slug: slug as string,
    status: preview ? undefined : PostStatus.PUBLISHED,
  },
  options: {
    transform: response => get(response, 'blog_article'),
  },
});

if (error.value || !data.value)
  throw createError({
    statusCode: STATUS_CODES.NOT_FOUND,
  });

const title = computed(() => get(data.value, 'data.title'));
const subtitle = computed(() => get(data.value, 'data.data.post_subtitle'));
const location = computed(() => get(data.value, 'data.data.location'));
const publishedAt = computed(() => get(data.value, 'publish_at'));
const markup = computed(() => get(data.value, 'data.content'));
const seo = computed(() => get(data.value, 'data.seo', {}));

useSeoMeta({
  title: get(seo.value, 'title'),
  ogTitle: get(seo.value, 'title'),
  description: get(seo.value, 'description'),
  ogDescription: get(seo.value, 'description'),
  ogImage: get(seo.value, 'image'),
  ogType: get(seo.value, 'og_type'),
});
</script>

<style scoped lang="scss">
@use '_/mixins/flex';
@use '_/mixins/size';
@use '_/mixins/typo';
@use '_/mixins/media';
@use '_/mixins/component';
@use '_/fn';

@mixin list-item {
  @include typo.body-2;

  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
}

[data-page-name='press-release'] {
  padding: 2rem 0 8rem;

  .wrapper {
    padding: 0 2rem;

    @include size.wrapper(80rem);

    @include media.mobile {
      padding: 0 1rem;
    }

    .content {
      @include flex.start-start;

      flex-direction: column;
      gap: 2rem 0;

      @include size.fluid-max-width(50rem);

      margin-bottom: 2rem;

      @include media.tablet {
        gap: 1.5rem 0;

        @include size.fluid-max-width(100%);
      }

      @include media.mobile {
        gap: 1rem 0;
      }

      [data-component-name='AppBreadcrumbs'] {
        max-width: 100%;

        @include media.mobile {
          margin-bottom: 0.5rem;
        }
      }

      h1.title {
        @include typo.h3;
      }

      strong.subtitle {
        @include typo.subtitle-italic-2;
      }

      hr {
        border: none;
        height: 1px;
        width: 100%;
        background-color: fn.token('outline-secondary');
      }

      .tags {
        @include flex.center-start;

        gap: 0 0.5rem;

        [data-component-name='AppTag'] {
          &.published-at {
            svg {
              fill: fn.token('chip-primary-icon');
            }
          }
        }
      }

      // todo: replace w/ ArticleMarkup
      &:deep(.markup) {
        @include flex.start-start;

        flex-direction: column;
        gap: 2rem;

        @include media.tablet {
          gap: 1.5rem;
        }

        @include media.mobile {
          gap: 1rem;
        }

        h2 {
          @include typo.subtitle-1;
        }

        h3 {
          @include typo.subtitle-2;
        }

        p {
          color: fn.token('text-primary');

          @include typo.body-2;

          strong {
            @include typo.subtitle-2;
          }

          > br {
            display: none;
          }

          &:has(strong ~ br) {
            &:not(:last-of-type) {
              strong {
                display: block;

                @include typo.subtitle-1;

                margin-bottom: 2rem;

                @include media.tablet {
                  bottom: 1.5rem;
                }

                @include media.mobile {
                  margin-bottom: 1rem;
                }
              }
            }

            // press contact (last block)
            &:last-of-type {
              br {
                display: initial;
              }
            }
          }
        }

        a {
          color: fn.token('link');

          @include typo.link-text-2;

          &:hover {
            background-color: rgb(fn.token('link'), 16%);
          }
        }

        ul {
          margin-left: 1.625rem;
          list-style-type: disc;

          li {
            @include list-item;
          }
        }
      }
    }
  }
}
</style>
